import { useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1533923156502-be31530547c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState("");
  const isRegister = mode === "register";
  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => (new URLSearchParams(window.location.search).get("google") === "failed" ? "Google sign-in failed. Please try again." : ""));
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister && password !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    const err = isRegister ? await register(name, email, password) : await login(email, password);
    setBusy(false);
    if (err) setError(err);
    else navigate("/");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Basketball arena" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14]/40 to-[#0B0E14]" />
        <div className="relative h-full flex flex-col justify-end p-14">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-400">Pregame only · No live data</p>
          <h2 className="font-display text-6xl font-black uppercase leading-[0.9] mt-3">Every line,<br />priced before<br />tip-off.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 court-grain">
        {mode === "forgot" ? (
          <div className="w-full max-w-sm fade-up"><ForgotPasswordForm initialEmail={email} onBack={() => setMode("login")} /></div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <BrandLogo />
          <div>
            <h1 className="font-display text-4xl font-extrabold uppercase" data-testid="auth-title">{isRegister ? "Create account" : "Sign in"}</h1>
            <p className="text-sm text-slate-400 mt-1">{isRegister ? "Free access to today's NBA player projections." : "NBA player projections — sign in to your account."}</p>
          </div>
          <div className="space-y-3">
            {isRegister && <Input data-testid="register-name-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} autoComplete="name" className="h-11 bg-slate-900/70" />}
            <Input data-testid="login-email-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 bg-slate-900/70" />
            <Input data-testid="login-password-input" type="password" placeholder={isRegister ? "Password (min 8 characters)" : "Password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={isRegister ? 8 : undefined} autoComplete={isRegister ? "new-password" : "current-password"} className="h-11 bg-slate-900/70" />
            {isRegister && <Input data-testid="register-confirm-input" type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" className="h-11 bg-slate-900/70" />}
          </div>
          {!isRegister && (
            <div className="-mt-3 text-right">
              <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }}
                className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors">Forgot password?</button>
            </div>
          )}
          {error && <p data-testid="login-error" className="text-sm text-rose-400">{error}</p>}
          <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-blue-600 hover:bg-blue-500 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isRegister ? "Create account" : "Sign in"}
          </Button>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-slate-500"><span className="h-px flex-1 bg-slate-800" />or<span className="h-px flex-1 bg-slate-800" /></div>
          <button type="button" data-testid="google-login-btn" onClick={googleSignIn}
            className="w-full h-11 rounded-md border border-slate-700 bg-white text-slate-900 font-semibold flex items-center justify-center gap-3 transition-colors hover:bg-slate-100">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <p className="text-sm text-slate-400 text-center">
            {isRegister ? "Already have an account?" : "New here?"}{" "}
            <button type="button" data-testid="auth-mode-toggle" onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }}
              className="font-semibold text-blue-400 hover:text-blue-300 transition-colors">
              {isRegister ? "Sign in" : "Create an account"}
            </button>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
